import { Box } from '@mui/material';
import { useCallback, useEffect, useState, useRef } from 'react';

interface ResizableDrawerContentProps {
  children: React.ReactNode;
  defaultWidth?: number;
  onWidthChange?: (width: number) => void;
}

const MOBILE_BREAKPOINT = 900;

export const ResizableDrawerContent = ({
  children,
  defaultWidth = 650,
  onWidthChange,
}: ResizableDrawerContentProps) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT;
  const [width, setWidth] = useState(
    isMobile ? defaultWidth : Math.min(defaultWidth, window.innerWidth - 300),
  );
  const [isDragging, setIsDragging] = useState(false);
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && isMounted.current) {
        // For right-side drawer, calculate width from right edge
        const newWidth = window.innerWidth - e.clientX;
        const clampedWidth = Math.min(Math.max(newWidth, 400), window.innerWidth - 200);
        setWidth(clampedWidth);
        onWidthChange?.(clampedWidth);
      }
    };

    const handleMouseUp = () => {
      if (isMounted.current) {
        setIsDragging(false);
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        // Mobile: fill the drawer. Desktop: use pixel width.
        width: { xs: '100%', md: width },
        maxWidth: '100%',
      }}
    >
      {children}
      {/* Resize Handle — desktop only */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          left: -4,
          top: 0,
          bottom: 0,
          width: 8,
          cursor: 'col-resize',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
          '&:hover .drag-handle': {
            opacity: 1,
          },
          zIndex: 1,
        }}
      >
        <Box
          className="drag-handle"
          sx={{
            width: 2,
            height: '100%',
            backgroundColor: 'divider',
            opacity: 0,
            transition: 'opacity 0.2s',
          }}
        />
      </Box>
    </Box>
  );
};
